import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../hooks/useTranslation'

export function LoginPage() {
  const { login } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await login(email, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error_generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg-body flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / title */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-primary-red rounded-md flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-h3">H</span>
          </div>
          <h1 className="text-h2 font-bold text-text-primary">{t('auth.title')}</h1>
          <p className="text-body text-text-secondary mt-1">{t('auth.subtitle')}</p>
        </div>

        {/* Card */}
        <div className="bg-bg-surface border border-border-subtle rounded-xl p-8 shadow-2xl">
          {/* Google — disabled */}
          <div className="relative group mb-4">
            <button
              type="button"
              disabled
              aria-label={t('auth.google_unavailable')}
              className="w-full flex items-center justify-center gap-3 px-4 py-2 bg-border-standard text-text-tertiary rounded-md cursor-not-allowed opacity-60 text-body font-medium"
            >
              <GoogleIcon />
              {t('auth.google_login')}
            </button>
            <span
              role="tooltip"
              className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-bg-surface-hover border border-border-standard text-caption text-text-secondary rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              {t('auth.coming_soon')}
            </span>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-border-subtle" />
            <span className="text-caption text-text-tertiary">{t('auth.divider')}</span>
            <div className="flex-1 h-px bg-border-subtle" />
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-3">
              <div>
                <label htmlFor="login-email" className="block text-caption text-text-secondary mb-1">
                  {t('auth.email_label')}
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email_placeholder')}
                  className="w-full px-3 py-2 bg-bg-input border border-border-standard rounded-md text-text-primary placeholder:text-text-tertiary text-body focus:outline-none focus:border-border-strong focus:ring-2 focus:ring-primary-red/10 transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block text-caption text-text-secondary mb-1"
                >
                  {t('auth.password_label')}
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password_placeholder')}
                  className="w-full px-3 py-2 bg-bg-input border border-border-standard rounded-md text-text-primary placeholder:text-text-tertiary text-body focus:outline-none focus:border-border-strong focus:ring-2 focus:ring-primary-red/10 transition-colors"
                />
              </div>

              {error && (
                <p role="alert" className="text-caption text-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2 bg-primary-red hover:bg-primary-red-dark disabled:bg-border-standard disabled:text-text-tertiary disabled:cursor-not-allowed text-white font-medium rounded-md text-body transition-colors"
              >
                {isSubmitting ? t('auth.logging_in') : t('auth.login_button')}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-caption text-text-tertiary mt-6">{t('auth.powered_by')}</p>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z"
      />
    </svg>
  )
}
